






































































































































import { Component, Watch } from "vue-property-decorator";
import {
  loadClient,
  setLogoFile,
  updateClient,
  isMemberClient,
} from "@/data/repository/ClientRepository";

import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import LogoPreviewComponent from "@/components/common/LogoPreview.vue";
import { CompanyLogo } from "@/data/models/Settings";
import { loadReportSettings, updateReportSettings } from "@/data/repository/SettingsRepository";
import { filterOutUndefined } from "@/utils/Utils";
import { validateEmailAddresses } from "@/utils/EmailUtils";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import SettingsPageMixin from "@/components/settings/SettingsPageMixin";
import AddressPositionFieldComponent from "@/components/common/AddressPositionField.vue";
import { GeoLocation, PickedAddressParts, PickerAddress } from "@/utils/GeocodingService";
import { hasCoordinatesChanged } from "@/utils/UserFormUtils";
import { addressPositionAccess } from "@/permissions/UserPermissions";
import { getEffectiveUser } from "@/utils/ViewAs";

/**
 * Interface defining company data.
 */
interface CompanyData {
  shortName?: string;
  companyName: string;
  street: string;
  houseNumber: string;
  zip: string;
  city: string;
  country?: string | null;
  department: string;

  /**
   * Position of the company address. Filled in automatically by IRES Cloud from the
   * address, or placed by hand on the map, which records quality MANUAL and stops the
   * automatic geocoding from moving it again.
   */
  coordinates?: GeoLocation | null;

  companyEmailAddress?: string;
  companyPhone?: string;
  companyIdentifier?: string;

  logoFile?: string | null;
  scaleFactor?: number;
}

@Component({
  components: {
    "logo-preview": LogoPreviewComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
    "address-position-field": AddressPositionFieldComponent,
  },
})

export default class CompanyDataComponent extends SettingsPageMixin {
  isSaveDisabled = false;

  shortName = "";
  companyName = "";
  street = "";
  houseNumber = "";
  zip = "";
  city = "";
  country = "";
  department = "";

  coordinates: GeoLocation | null = null;

  companyEmailAddress = "";
  companyPhone = "";
  companyIdentifier = "";

  logoFile: string | null = null;
  newLogoFile: File | null = null;
  scaleFactor = 100;

  errorCompanyEmailAddress: string[] = [];
  errorCompanyIdentifier: string[] = [];

  initialCompanyData: CompanyData = this.getInitialCompanyData();

  @Watch("companyEmailAddress")
  onCompanyEmailAddressChanged() {
    this.resetErrors();
  }

  get hasChanged() {
    return (
      this.shortName !== (this.initialCompanyData.shortName ?? "") ||
      this.companyName !== this.initialCompanyData.companyName ||
      this.street !== this.initialCompanyData.street ||
      this.houseNumber !== this.initialCompanyData.houseNumber ||
      this.zip !== this.initialCompanyData.zip ||
      this.city !== this.initialCompanyData.city ||
      this.country !== (this.initialCompanyData.country ?? "") ||
      hasCoordinatesChanged(this.coordinates, this.initialCompanyData.coordinates) ||
      this.department !== this.initialCompanyData.department ||
      this.companyEmailAddress !== this.initialCompanyData.companyEmailAddress ||
      this.companyPhone !== this.initialCompanyData.companyPhone ||
      this.companyIdentifier !== this.initialCompanyData.companyIdentifier ||
      this.logoFile !== this.initialCompanyData.logoFile ||
      this.newLogoFile !== null ||
      this.scaleFactor !== this.initialCompanyData.scaleFactor
    );
  }

  get isMemberClient() {
    return isMemberClient();
  }

  getInitialCompanyData() {
    return {
      shortName: this.shortName,
      companyName: this.companyName,
      street: this.street,
      houseNumber: this.houseNumber,
      zip: this.zip,
      city: this.city,
      country: this.country,
      coordinates: this.coordinates,
      department: this.department,
      companyEmailAddress: this.companyEmailAddress,
      companyPhone: this.companyPhone,
      companyIdentifier: this.companyIdentifier,
      logoFile: this.logoFile,
      scaleFactor: this.scaleFactor,
    } as CompanyData;
  }

  initializeParameters(companyData: CompanyData, companyLogo?: CompanyLogo) {
    this.shortName = companyData.shortName ?? "";
    this.companyName = companyData.companyName;
    this.street = companyData.street;
    this.houseNumber = companyData.houseNumber;
    this.zip = companyData.zip;
    this.city = companyData.city;
    this.country = companyData.country ?? "";
    this.coordinates = companyData.coordinates ?? null;
    this.department = companyData.department;

    this.companyEmailAddress = companyData.companyEmailAddress ?? "";
    this.companyPhone = companyData.companyPhone ?? "",
    this.companyIdentifier = companyData.companyIdentifier ?? "";
    this.logoFile = companyLogo?.logoFile ?? null;
    this.scaleFactor = companyLogo?.scaleFactor ?? 100;
    this.newLogoFile = null;

    this.initialCompanyData = this.getInitialCompanyData();
  }

  resetErrors() {
    if (this.companyEmailAddress.length !== 0) {
      this.errorCompanyEmailAddress = [];
    }
  }
  
  validateCompanyIdentifier(inputValue: string) {
    // A regular expression to allow only alphanumeric characters
    const validInput = inputValue.replace(/[^a-zA-Z0-9]/g, '');

    if (validInput !== this.companyIdentifier) {
      this.errorCompanyIdentifier.push(this.$t("settings.companySettings.errorCompanyIdentifier").toString());
      this.isSaveDisabled = true;
    }
    else {
      this.errorCompanyIdentifier = [];
      this.isSaveDisabled = false;
    }
  }

  async created() {
    const [client, reportSettings] = await Promise.all([
      loadClient(),
      loadReportSettings(),
    ]);

    if (!client) {
      return;
    }

    const companyData = client as CompanyData;
    const companyLogo: CompanyLogo = reportSettings?.companyLogo ?? {
      logoFile: client.logoFile ?? null,
      scaleFactor: 100,
    };

    this.initializeParameters(companyData, companyLogo);

    this.isEditMode = false;
  }

  async doSave() {
    let isValid = true;

    if (this.companyEmailAddress !== "") {
      if (!validateEmailAddresses(this.companyEmailAddress)) {
        isValid = false;
        this.errorCompanyEmailAddress.push(this.$t("settings.companySettings.errorEmailAddress").toString());
      }
    }

    if (!isValid) {
      SnackbarModule.showError({ key: "common.changesNotSaved" });
      throw new Error("validation");
    }
    
    const update: CompanyData = filterOutUndefined({
      shortName: this.shortName !== ""
        ? this.shortName
        : undefined,
      companyName: this.companyName,
      street: this.street,
      houseNumber: this.houseNumber,
      zip: this.zip,
      city: this.city,
      // null, not undefined: filterOutUndefined would drop an undefined and leave a
      // cleared country still stored.
      country: this.country !== "" ? this.country : null,
      // Omitted when there is no position, so the Cloud trigger geocodes the address
      // itself. Writing an explicit null would read as "already looked up, no result"
      // and suppress that lookup for good.
      coordinates: this.coordinates ?? undefined,
      department: this.department,
      companyEmailAddress: this.companyEmailAddress !== ""
        ? this.companyEmailAddress
        : undefined,
      companyPhone: this.companyPhone !== ""
        ? this.companyPhone
        : undefined,
      companyIdentifier: this.companyIdentifier !== ""
        ? this.companyIdentifier
        : undefined,
    });

    if (this.newLogoFile) {
      this.logoFile = await setLogoFile(this.newLogoFile);
      this.newLogoFile = null;
    }

    await Promise.all([
      updateClient(update),
      updateReportSettings({
        companyLogo: {
          logoFile: this.logoFile,
          scaleFactor: this.scaleFactor,
        },
      }),
    ]);

    this.initialCompanyData = this.getInitialCompanyData();
  }

  /** Filled pin once the address has a position, outline while it has none. */
  /**
   * Whether geocoding is switched on for this client at all.
   *
   * Reactive: a capability change on the current user must reach this getter without a page
   * refresh (see the lessons in CLAUDE.md).
   */
  get enableGeoEncoding(): boolean {
    return addressPositionAccess(getEffectiveUser());
  }

  /** The company address as currently entered, for the map affordance. */
  get enteredAddress(): PickerAddress {
    return {
      street: this.street,
      houseNumber: this.houseNumber,
      zipCode: this.zip,
      city: this.city,
      country: this.country,
    };
  }

  /**
   * The company address as saved.
   *
   * A pin placed for it must not decide where the map opens once the address has been
   * edited.
   */
  /**
   * Fills in the address parts a lookup or the map found, for the ones that were left empty:
   * the postcode and city from the automatic lookup, or the whole address from a pin placed
   * on the map while the address was still empty (ticket 2610025).
   *
   * Only ever fills a blank: a value the user typed stands, even where Google disagrees.
   */
  onAddressResolved(parts: PickedAddressParts) {
    if (parts.street && !this.street) {
      this.street = parts.street;
    }

    if (parts.houseNumber && !this.houseNumber) {
      this.houseNumber = parts.houseNumber;
    }

    if (parts.zipCode && !this.zip) {
      this.zip = parts.zipCode;
    }

    if (parts.city && !this.city) {
      this.city = parts.city;
    }

    if (parts.country && !this.country) {
      this.country = parts.country;
    }
  }

  get savedAddress(): PickerAddress {
    return {
      street: this.initialCompanyData.street,
      houseNumber: this.initialCompanyData.houseNumber,
      zipCode: this.initialCompanyData.zip,
      city: this.initialCompanyData.city,
      country: this.initialCompanyData.country,
    };
  }

  onLogoUpload(file: File) {
    this.newLogoFile = file;
  }

  onRemoveLogo() {
    this.newLogoFile = null;
    this.logoFile = null;
  }

  mounted() {
    this.setToolbarTitle(this.$t("settings.companySettings.companyData").toString());
  }
}
